import React, { useEffect, useState, useRef, useMemo } from 'react';
import './Triage.scss';
import MeasuresTable from './MeasuresTable';
import HelperTables from './HelperTables';

// Import JSON data directly
import appsData from './data/canadian-pediatric-ed-triage-apps.json';
import kaplanData from './data/canadian-pediatric-ed-triage-kaplan.json';
import assutaData from './data/canadian-pediatric-ed-triage-assuta.json';
import emekData from './data/canadian-pediatric-ed-triage-emek.json';
import validMeasuresData from './data/valid-measures-apps.json';

import { faMagnifyingGlass, faSyringe, faTable, faUserDoctor } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../components/Footer';
import FooterItem from '../../components/FooterItem';
import AppsHeader from '../components/AppsHeader';

// Type definitions
interface Category {
  categoryId: string;
  categoryDisplay: string;
}

interface TriageItem {
  category: string;
  description: string;
  level: number;
  categoryDisplay: string;
  $isRecent?: boolean;
}

// The structure should match the imported JSON files
interface JsonData {
  states: Array<{
    state: string;
    state_display_name?: string;
    'immediate-0'?: { symptoms: string[] };
    'level2-15'?: { symptoms: string[] };
    'level3-30'?: { symptoms: string[] };
    'level4-60'?: { symptoms: string[] };
    'level5-120'?: { symptoms: string[] };
    [key: string]: any;
  }>;
}

// Main component
interface TriageProps {
  hospital?: string;
}

const Triage: React.FC<TriageProps> = ({ hospital = 'apps' }) => {
  const [searchText, setSearchText] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);  
  const [triageData, setTriageData] = useState<TriageItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [itemsShowingState, setItemsShowingState] = useState<string>('RECENTLY_USE');
  const [dataShown, setDataShown] = useState<string>('TRIAGE');
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);  
  const [forceUpdate, setForceUpdate] = useState<number>(0);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const colorByLevel: Record<number, string> = {
    1: "#FE0000",
    2: "#ED4400",
    3: "#b6bf50",
    4: "#44D113",
    5: "#3594E8"
  };

  // Function to flatten the JSON data
  const flattenJson = (inputJson: JsonData): TriageItem[] => {
    let outputJson: TriageItem[] = [];
    inputJson.states.forEach(stateItem => {
      const state = stateItem.state;
      const state_display = stateItem.state_display_name;
      Object.keys(stateItem).forEach(key => {
        if (key !== 'state' && key !== 'state_display_name') {
          let parsedlevel = key.split('-')[0];
          const level = parsedlevel === "immediate" ? 1 : parseInt(parsedlevel.substring(5));
          const symptoms = stateItem[key].symptoms;

          symptoms.forEach((symptom: string) => {
            outputJson.push({
              category: state,
              description: symptom,
              level: level,
              categoryDisplay: state_display ? state_display : state
            });
          });
        }
      });
    });
    return outputJson;
  };

  // Function to get categories from triage data
  const getCategories = (data: TriageItem[]): Category[] => {
    const categoriesSet = new Set<string>();
    const result: Category[] = [];

    for (let i = 0; i < data.length; i++) {
      const currData = data[i];
      const category = currData.categoryDisplay;
      if (categoriesSet.has(category)) {
        continue;
      }
      categoriesSet.add(category);
      result.push({
        categoryId: currData.category,
        categoryDisplay: currData.categoryDisplay
      });
    }

    return result;
  };

  // Use imported JSON data
  useEffect(() => {
    const loadData = () => {
      try {
        // Get the appropriate JSON data based on the hospital parameter
        let triageJsonData;

        switch (hospital) {
          case 'kaplan':
            triageJsonData = kaplanData;
            break;
          case 'assuta':
            triageJsonData = assutaData;
            break;
          case 'emek':
            triageJsonData = emekData;
            break;
          case 'apps':
          default:
            triageJsonData = appsData;
            break;
        }

        // Flatten the data
        const flattenedData = flattenJson(triageJsonData);
        
        // Update state with data
        setTriageData(flattenedData);
        setCategories(getCategories(flattenedData));

      } catch (error) {
        console.log('Error loading triage data:', error);
      }
    };

    loadData();
  }, [hospital]);

  // Check if item should be shown based on current filter state
  const showItem = (item: TriageItem): boolean => {
    switch (itemsShowingState) {
      case 'CATEGORY_FILTER':
        return selectedCategories.includes(item.category);

      case 'SEARCH_FILTER':
        return item.description.toUpperCase().indexOf(searchText.toUpperCase()) > -1;

      default:
        return false;
    }
  };

  // Toggle filter function
  const toggleFilter = (categoryId: string): void => {
    let newSelectedCategories: string[];

    // Check if the category is already selected
    if (selectedCategories.includes(categoryId)) {
      // If it is, clear the selection
      newSelectedCategories = [];
    } else {
      // If it isn't, clear previous selections and select only this category
      newSelectedCategories = [categoryId];
    }

    // Update state
    setSelectedCategories(newSelectedCategories);

    setItemsShowingState('CATEGORY_FILTER');
    setForceUpdate(prev => prev + 1); // Force re-render

  };

  // Clear selected categories
  const clearSelectedCategories = (): void => {
    setSelectedCategories([]);
  };

  // Open search bar
  const openSearchBar = (): void => {
    setDataShown('TRIAGE');
    setSearchBarOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  // Close search bar
  const closeSearchBar = (): void => {
    setSearchBarOpen(false);
    setSearchText('');
    if (selectedCategories.length > 0) {
      setItemsShowingState('CATEGORY_FILTER');
    } else {
      setItemsShowingState('RECENTLY_USE');
    }
  };

  // Handle search input
  const searchTyping = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      closeSearchBar();
      return;
    }

    if (searchText.length > 0) {
      clearSelectedCategories();
      setItemsShowingState('SEARCH_FILTER');
    }
  };

  // Close table
  const closeTable = (): void => {
    setDataShown('TRIAGE');
  };

  // Calculate filtered items whenever dependencies change
  const filteredItems = useMemo(() => {
    return triageData
      .filter(showItem)
      .sort((a, b) => a.level - b.level);
  }, [triageData, itemsShowingState, selectedCategories, searchText, forceUpdate]);

  return (
    <div className="app-with-footer">
      <div className="triage">
        {dataShown === 'TRIAGE' && (
          <div id="triage-main">
            <div>
              <AppsHeader 
                title="טריאז' - מיון ילדים"
              />
              <div id="filters" className="filters-group">
                {categories.map((category) => (
                  <div
                    key={category.categoryId}
                    className={`chip ${selectedCategories.includes(category.categoryId) ? 'selected' : ''}`}
                    onClick={() => toggleFilter(category.categoryId)}
                  >
                    {category.categoryDisplay}
                  </div>
                ))}
              </div>
              <div className="list-conditions" id="list-conditions">
                <ol className="list-group" style={{ padding: 0 }}>
                  {filteredItems.map((item) => (
                    <li
                      key={`${item.category}-${item.description}-${item.level}`}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {item.description}
                      <span
                        className="badge text rounded-pill badge-triage"
                        style={{
                          backgroundColor: colorByLevel[item.level],
                          fontSize: '1rem'
                        }}
                      >
                        {item.level}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}

        {dataShown === 'MEASURES' && (
          <MeasuresTable
            onClose={closeTable}
            validMeasures={validMeasuresData}
          />
        )}

        {dataShown === 'HELPER_TABLES' && (
          <HelperTables
            onClose={closeTable}
          />
        )}
      </div>
      <Footer>
        {!searchBarOpen ? (<>
          <FooterItem
            isActive={false}
            icon={faMagnifyingGlass}
            label="חפש"
            onClick={openSearchBar}
          />
          <FooterItem
            isActive={dataShown === 'TRIAGE'}
            icon={faUserDoctor}
            label="טריאז'"
            onClick={() => {setDataShown('TRIAGE');}}
          ></FooterItem>
          <FooterItem
            isActive={dataShown === 'MEASURES'}
            icon={faSyringe}
            label="מדדים"
            onClick={() => {setDataShown('MEASURES');}}
          >
          </FooterItem>
          <FooterItem
            isActive={dataShown === 'HELPER_TABLES'}
            icon={faTable}
            label="טבלאות עזר"
            onClick={() => {setDataShown('HELPER_TABLES');}}
          ></FooterItem>
          </>)
          : (
            <div id="searchbox-container" className="triage searchbox-container" style={{width: '100%'}}>
              <input
                type="text"
                className="search-input"
                id="searchInput"
                ref={searchInputRef}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyUp={searchTyping}
                placeholder="חיפוש לפי כל הערכים.."
              />
              <div className="closeButtonDiv">
                <button className="closeButton" onClick={closeSearchBar}>
                  ×
                </button>
              </div>
            </div>
          )}
      </Footer>
    </div>
  );
};

export default Triage;